import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Field, Input, Modal, Select, Textarea } from '../../components/ui';
import { ApiError } from '../../lib/api';
import {
  materialFormSchema,
  emptyMaterialForm,
  UNIT_SUGGESTIONS,
  type MaterialFormValues,
} from './schema';
import { useCreateMaterial, useUpdateMaterial } from './api';
import type { Material } from './types';
import { useLocations } from '../warehouse/api';

export default function MaterialForm({
  material,
  onClose,
  initialBarcode,
  onCreated,
}: {
  material: Material | null; // null = nový
  onClose: () => void;
  // Ze skeneru: neznámý čárový kód rovnou předvyplní nový materiál.
  initialBarcode?: string;
  onCreated?: (m: Material) => void;
}) {
  const { data: locations } = useLocations();
  const isEdit = material !== null;
  const create = useCreateMaterial();
  const [record] = useState(material ?? {}); // stav záznamu při otevření, ať se pozná cizí změna
  const update = useUpdateMaterial();
  const pending = create.isPending || update.isPending;
  const serverError = (create.error ?? update.error) as ApiError | null;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, defaultValues },
  } = useForm<MaterialFormValues>({
    resolver: zodResolver(materialFormSchema),
    defaultValues: material
      ? {
          sku: material.sku,
          name: material.name,
          category: material.category ?? '',
          unit: material.unit,
          stockQty: material.stockQty,
          minQty: material.minQty ?? '',
          pricePerUnit: material.pricePerUnit ?? '',
          currency: material.currency,
          supplier: material.supplier ?? '',
          supplierUrl: material.supplierUrl ?? '',
          location: material.location ?? '',
          notes: material.notes ?? '',
          barcode: material.barcode ?? '',
          locationId: material.locationId ?? '',
        }
      : { ...emptyMaterialForm, barcode: initialBarcode ?? '' },
  });

  const onSubmit = handleSubmit((values) => {
    const onDone = { onSuccess: () => onClose() };
    if (isEdit && material) update.mutate({ id: material.id, values, edit: { start: defaultValues as MaterialFormValues, record } }, onDone);
    else create.mutate(values, { onSuccess: (m) => { onCreated?.(m as Material); onClose(); } });
  });

  return (
    <Modal title={isEdit ? 'Upravit materiál' : 'Nový materiál'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <datalist id="unitSuggestions">
          {UNIT_SUGGESTIONS.map((u) => (
            <option key={u} value={u} />
          ))}
        </datalist>

        <div className="grid grid-cols-[8rem_1fr] gap-3">
          <Field label="SKU *" htmlFor="sku" error={errors.sku?.message}>
            <Input id="sku" autoFocus className="font-mono" {...register('sku')} />
          </Field>
          <Field label="Název *" htmlFor="name" error={errors.name?.message}>
            <Input id="name" {...register('name')} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Kategorie" htmlFor="category" error={errors.category?.message}>
            <Input id="category" {...register('category')} />
          </Field>
          <Field label="Jednotka *" htmlFor="unit" error={errors.unit?.message}>
            <Input id="unit" list="unitSuggestions" {...register('unit')} />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {!isEdit ? (
            <Field label="Počáteční stav" htmlFor="stockQty" error={errors.stockQty?.message} hint="Kolik už máš skladem.">
              <Input id="stockQty" inputMode="decimal" {...register('stockQty')} />
            </Field>
          ) : (
            <Field label="Skladem" htmlFor="stockDisplay" hint='Měň přes „Sklad" (naskladnit / korekce).'>
              <Input id="stockDisplay" value={`${material?.stockQty ?? ''} ${material?.unit ?? ''}`.trim()} disabled readOnly />
            </Field>
          )}
          <Field
            label="Min. zásoba"
            htmlFor="minQty"
            error={errors.minQty?.message}
            hint="Pod ní červeně."
          >
            <Input id="minQty" inputMode="decimal" {...register('minQty')} />
          </Field>
          <Field
            label={isEdit ? 'Orientační cena/j.' : 'Cena/jedn. (nákup)'}
            htmlFor="pricePerUnit"
            error={errors.pricePerUnit?.message}
            hint={isEdit ? 'Ocenění drží průměr z naskladnění.' : undefined}
          >
            <Input id="pricePerUnit" inputMode="decimal" {...register('pricePerUnit')} />
          </Field>
        </div>

        <div className="grid grid-cols-[1fr_5rem] gap-3">
          {/* Pozice = regál / police se štítkem. Staré volné umístění jen u starších záznamů. */}
          <Field label="Pozice" htmlFor="locationId" hint="Regál / police — štítky a správa v Sklad → Pozice.">
            <Controller
              control={control}
              name="locationId"
              render={({ field }) => (
                <Select id="locationId" value={field.value} onChange={field.onChange}>
                  <option value="">— bez pozice —</option>
                  {(locations ?? []).map((l) => (
                    <option key={l.id} value={l.id}>{l.code}{l.name ? ` · ${l.name}` : ''}</option>
                  ))}
                </Select>
              )}
            />
          </Field>
          <Field label="Čárový kód výrobce" htmlFor="barcode" error={errors.barcode?.message} hint="EAN z obalu — skener ho pak pozná. Jde přiřadit i skenem.">
            <Input id="barcode" inputMode="numeric" {...register('barcode')} />
          </Field>
          {material?.location && (
            <Field label="Staré umístění (text)" htmlFor="location" error={errors.location?.message} hint="Z doby před pozicemi — po přiřazení pozice ho můžeš smazat.">
              <Input id="location" {...register('location')} />
            </Field>
          )}
          
          <Field label="Měna" htmlFor="currency" error={errors.currency?.message}>
            <Input id="currency" className="uppercase" maxLength={3} {...register('currency')} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Dodavatel" htmlFor="supplier" error={errors.supplier?.message}>
            <Input id="supplier" {...register('supplier')} />
          </Field>
          <Field label="Odkaz dodavatele" htmlFor="supplierUrl" error={errors.supplierUrl?.message}>
            <Input id="supplierUrl" type="url" placeholder="https://…" {...register('supplierUrl')} />
          </Field>
        </div>

        <Field label="Poznámky" htmlFor="notes" error={errors.notes?.message}>
          <Textarea id="notes" rows={2} {...register('notes')} />
        </Field>

        {serverError && (
          <p className="text-sm text-red-600 dark:text-red-400">{serverError.message}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            Zrušit
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? 'Ukládám…' : isEdit ? 'Uložit' : 'Vytvořit'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
